import { safeParseApiError } from './parseApiError';

/**
 * Маркеры 422-ошибки «документ загружен до обновления, нужен re-upload».
 * Имена файлов идут после двоеточия через запятую. См. updates.md п. 3–4.
 */
const REUPLOAD_PATTERNS = [
  /необходимо загрузить повторно[^:]*:\s*(.+)$/iu, // чат: create / calc-usage
  /перезагрузите файлы[^:]*:\s*(.+)$/iu, // presale: prefill / estimate
];

/** Извлекает массив `.message` из data.errors / data.violations / data.detail, если это массив. */
function extractMessages(data: unknown, key: 'errors' | 'violations' | 'detail'): string[] {
  if (!data || typeof data !== 'object') return [];
  const list = (data as Record<string, unknown>)[key];
  if (!Array.isArray(list)) return [];
  return list
    .map((item) =>
      item &&
      typeof item === 'object' &&
      typeof (item as { message?: unknown }).message === 'string'
        ? (item as { message: string }).message
        : null
    )
    .filter((m): m is string => m !== null);
}

/**
 * Собирает отдельные сообщения-кандидаты для проверки паттернов.
 * Каждое `errors[].message` проверяется отдельно, чтобы склейка через '. '
 * в generalMessage не «перетягивала» имена из соседних ошибок.
 * generalMessage идёт последним как fallback (когда структура нестандартная).
 */
function collectCandidateMessages(error: unknown): string[] {
  const data =
    error && typeof error === 'object' && 'data' in error
      ? (error as { data: unknown }).data
      : undefined;

  const candidates: string[] = [
    ...extractMessages(data, 'errors'),
    ...extractMessages(data, 'violations'),
    ...extractMessages(data, 'detail'),
  ];

  const { generalMessage } = safeParseApiError(error);
  candidates.push(generalMessage);
  return candidates;
}

/**
 * Если ошибка — 422 «нужен re-upload», возвращает имена проблемных файлов.
 * Иначе null. Если у ошибки есть status и он не 422 — сразу null.
 */
export function extractReuploadFileNames(error: unknown): string[] | null {
  const status =
    error && typeof error === 'object' && 'status' in error
      ? (error as { status: unknown }).status
      : undefined;
  if (typeof status === 'number' && status !== 422) return null;

  for (const message of collectCandidateMessages(error)) {
    for (const pattern of REUPLOAD_PATTERNS) {
      const match = message.match(pattern);
      if (match) {
        const names = match[1]
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (names.length > 0) return names;
      }
    }
  }
  return null;
}
